import { TimelineEvent, TimelineEventType, getEventDescription, isImageAttachment, isWorkOrderEventType } from '../types/timeline';
import { BuildingSummary } from '../types/protocols';
import {
    ReportDraft,
    ReportDraftFieldId,
    ReportDraftFilters,
    ReportDraftItem,
    ReportDraftItemField,
    ReportDraftItemPhoto,
    ReportDraftScope,
    ReportDraftTextBlock
} from '../types/reportDrafts';
import { getEventId } from './timelineEventIds';
import { parseDateString } from './timelineUtils';

/**
 * Pure report-draft logic: the seeding filter predicate, the snapshot builder,
 * and the render model. No Firebase imports — everything is unit-testable
 * offline, and both the draft editor UI and the PDF builder consume the same
 * render model so screen and print cannot drift (plan D7).
 *
 * Since D14, a draft is a self-contained snapshot: the live timeline is only
 * consulted at creation (seeding); the render model is built from the draft
 * alone.
 */

/** Format a date in Swedish for display and photo captions, e.g. "15 januari 2024". */
export function formatSwedishDate(date: Date): string {
    const months = [
        'januari', 'februari', 'mars', 'april', 'maj', 'juni',
        'juli', 'augusti', 'september', 'oktober', 'november', 'december'
    ];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * Does a building fall inside the draft's scope at seeding time (plan D12/D14:
 * membership and content are fixed once the draft is created).
 */
export function buildingMatchesScope(summary: BuildingSummary | undefined, scope: ReportDraftScope, buildingKey: string): boolean {
    switch (scope.type) {
        case 'all':
            return true;
        case 'tag':
            return !!scope.tagKey && !!summary?.groupTags && Object.keys(summary.groupTags).includes(scope.tagKey);
        case 'location': {
            if (!scope.locationLevel || !scope.locationKey) return false;
            const assigned = summary?.locationCategories?.[scope.locationLevel];
            // LocationCategory.key is typed String (boxed) in protocols — normalize
            return assigned !== undefined && String(assigned.key) === scope.locationKey;
        }
        case 'buildings':
            return !!scope.buildingKeys && scope.buildingKeys.includes(buildingKey);
        default:
            return false;
    }
}

/**
 * The single "does this event match the draft's filters" predicate, shared by
 * the filter form preview and draft seeding (plan F1).
 */
export function eventMatchesFilters(event: TimelineEvent, filters: ReportDraftFilters): boolean {
    const year = event.date.getFullYear();
    if (year < filters.startYear || year > filters.endYear) return false;
    if (filters.eventTypes && !filters.eventTypes.includes(event.eventType)) return false;
    return buildingMatchesScope(event.buildingSummary, filters.scope, event.buildingKey);
}

/** One photo of a live event, identified by its own Firebase map key. */
export interface ReportDraftPhoto {
    photoKey: string;
    storagePath: string;
    defaultCaption?: string;
}

/**
 * All image attachments of a live event, keyed for the snapshot (plan F3):
 * custom-event image attachments and work order after-pictures.
 * (TimelineEvent.componentPhotos is declared but never populated by any
 * builder, so the old export's component-photo handling was dead code and was
 * removed here — review F9.)
 */
export function getEventImagesWithKeys(event: TimelineEvent): ReportDraftPhoto[] {
    const images: ReportDraftPhoto[] = [];

    if (event.attachments) {
        for (const [photoKey, attachment] of Object.entries(event.attachments)) {
            if (isImageAttachment(attachment)) {
                images.push({
                    photoKey,
                    storagePath: attachment.storagePath,
                    defaultCaption: attachment.fileName
                });
            }
        }
    }

    if (event.afterPictures) {
        for (const [photoKey, photo] of Object.entries(event.afterPictures)) {
            if (photo.storagePath) {
                images.push({
                    photoKey,
                    storagePath: photo.storagePath,
                    defaultCaption: 'Slutförandefoto'
                });
            }
        }
    }

    return images;
}

/** Default printed lines of a live event — what the snapshot copies in. */
export function getDefaultDraftFields(event: TimelineEvent): { [field in ReportDraftFieldId]?: string } {
    const fields: { [field in ReportDraftFieldId]?: string } = {
        description: getEventDescription(event),
        buildingName: event.buildingName
    };

    if (isWorkOrderEventType(event.eventType) && event.completionComment) {
        fields.completionComment = event.completionComment;
    }

    if (event.eventType === TimelineEventType.INSPECTION_COMPLETED && event.inspectionMeasurement) {
        const m = event.inspectionMeasurement;
        const status = m.withinRange ? 'OK' : 'Utanför gränsvärden';
        fields.measurement = `Mätvärde: ${m.value} ${m.unit} (${status})`;
    }

    return fields;
}

/** Snapshot one live event into a self-contained draft item (plan D14). */
export function snapshotEvent(event: TimelineEvent): ReportDraftItem {
    const fields: { [field in ReportDraftFieldId]?: ReportDraftItemField } = {};
    for (const [field, text] of Object.entries(getDefaultDraftFields(event))) {
        if (text !== undefined) {
            fields[field as ReportDraftFieldId] = { text, original: text };
        }
    }

    const photos: { [photoKey: string]: ReportDraftItemPhoto } = {};
    getEventImagesWithKeys(event).forEach((photo, index) => {
        photos[photo.photoKey] = {
            storagePath: photo.storagePath,
            caption: photo.defaultCaption,
            sortOrder: index
        };
    });

    return {
        included: true,
        eventType: event.eventType,
        buildingKey: event.buildingKey,
        date: event.displayDate,
        inexactDate: event.inexactDate || undefined,
        fields,
        photos: Object.keys(photos).length > 0 ? photos : undefined
    };
}

/** Seed membership for a new draft: snapshot every live event matching the filters. */
export function computeSeedItems(liveEvents: TimelineEvent[], filters: ReportDraftFilters): { [eventId: string]: ReportDraftItem } {
    const items: { [eventId: string]: ReportDraftItem } = {};
    for (const event of liveEvents) {
        if (eventMatchesFilters(event, filters)) {
            items[getEventId(event)] = snapshotEvent(event);
        }
    }
    return items;
}

/** A snapshot photo resolved for rendering, in sortOrder. */
export interface ResolvedDraftPhoto {
    photoKey: string;
    storagePath: string;
    caption?: string;
    excluded: boolean;
}

/**
 * Final printed lines of an event. `null` = hidden; absent = not applicable
 * to this event type.
 */
export type ResolvedDraftFields = { [field in ReportDraftFieldId]?: string | null };

/** One member event of the draft, ready to render on screen or in the PDF. */
export interface ReportDraftRenderEvent {
    eventId: string;
    item: ReportDraftItem;
    /** Parsed snapshot date (epoch fallback for malformed data). */
    date: Date;
    /** false = excluded by the user; editor shows it greyed, PDF skips it. */
    included: boolean;
    comment?: string;
    fields: ResolvedDraftFields;
    photos: ResolvedDraftPhoto[];
    /** Event-anchored text blocks, in sortOrder, rendered after the event. */
    blocksAfter: ReportDraftTextBlock[];
}

export interface ReportDraftRenderYear {
    year: number;
    /** Year-anchored text blocks, in sortOrder, rendered before the events. */
    blocks: ReportDraftTextBlock[];
    /** Member events chronologically ascending (PDF order). */
    events: ReportDraftRenderEvent[];
}

export interface ReportDraftRenderModel {
    /** Document-anchored intro blocks, in sortOrder. */
    documentBlocks: ReportDraftTextBlock[];
    /** Ascending years; includes years that only carry a year-anchored block. */
    years: ReportDraftRenderYear[];
}

export function sortBlocks(blocks: ReportDraftTextBlock[]): ReportDraftTextBlock[] {
    return [...blocks].sort((a, b) => a.sortOrder - b.sortOrder || a.key.localeCompare(b.key));
}

function resolveFields(item: ReportDraftItem): ResolvedDraftFields {
    const fields: ResolvedDraftFields = {};
    for (const [field, value] of Object.entries(item.fields || {})) {
        fields[field as ReportDraftFieldId] = value.hidden ? null : value.text;
    }
    return fields;
}

function resolvePhotos(item: ReportDraftItem): ResolvedDraftPhoto[] {
    return Object.entries(item.photos || {})
        .sort(([, a], [, b]) => a.sortOrder - b.sortOrder)
        .map(([photoKey, photo]) => ({
            photoKey,
            storagePath: photo.storagePath,
            caption: photo.caption,
            excluded: photo.excluded === true
        }));
}

/**
 * Build the render model shared by the editor and the PDF — from the draft
 * alone; the live timeline is never consulted after creation (plan D14).
 */
export function buildReportDraftRenderModel(draft: ReportDraft): ReportDraftRenderModel {
    const blocks = Object.values(draft.textBlocks || {});
    const documentBlocks = sortBlocks(blocks.filter(b => b.anchorType === 'document'));
    const yearBlocks = blocks.filter(b => b.anchorType === 'year' && b.anchorYear !== undefined);
    const eventBlocks = blocks.filter(b => b.anchorType === 'event' && b.anchorEventId !== undefined);

    const memberEvents: ReportDraftRenderEvent[] = Object.entries(draft.items || {}).map(([eventId, item]) => ({
        eventId,
        item,
        date: parseDateString(item.date) || new Date(0),
        included: item.included,
        comment: item.comment,
        fields: resolveFields(item),
        photos: resolvePhotos(item),
        blocksAfter: sortBlocks(eventBlocks.filter(b => b.anchorEventId === eventId))
    }));

    memberEvents.sort((a, b) => a.date.getTime() - b.date.getTime() || a.eventId.localeCompare(b.eventId));

    // Years = union of member-event years and year-anchored block years, so a
    // year intro survives even if all its events are excluded.
    const yearSet = new Set<number>();
    memberEvents.forEach(e => yearSet.add(e.date.getFullYear()));
    yearBlocks.forEach(b => yearSet.add(b.anchorYear!));

    const years: ReportDraftRenderYear[] = Array.from(yearSet)
        .sort((a, b) => a - b)
        .map(year => ({
            year,
            blocks: sortBlocks(yearBlocks.filter(b => b.anchorYear === year)),
            events: memberEvents.filter(e => e.date.getFullYear() === year)
        }));

    return { documentBlocks, years };
}
